import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { NavLink } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { makeStyles } from "tss-react/mui";
import {
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useColorScheme } from "@mui/material/styles";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import StoreIcon from "@mui/icons-material/Store";

import { currentUserAtom } from "../../AuthProvider";
import { SubMenuName } from "../interface";
import { getUrl, stringAvatar } from "./utils";
import { getCurrentLocale } from "../../Locale";
import {
  KNOWLEDGE_BASE_MENU_LINKS,
  MARKETPLACE_MENU_LINKS,
} from "./menusStructure";
import ProfileMenu from "./ProfileMenu";
import ProfileSettingsDialog from "../../ProfileSettingsDialog/ProfileSettingsDialog";

export const navIconStyles = makeStyles()((theme) => ({
  menuItem: {
    color: theme.vars.palette.menu.text,
    display: "block",
    whiteSpace: "pre-wrap",
    height: "36px",
    "& .MuiSelect-select": {
      color: theme.vars.palette.menu.text,
    },
    "& .MuiInputBase-root:before, & .MuiInputBase-root:hover:before": {
      borderColor: theme.vars.palette.menu.text,
    },
    "@media only screen and (max-height: 768px)": {
      "&.MuiListItem-root": {
        height: "32px",

        "& svg": {
          height: "22px",
        },

        "& .MuiTypography-root": {
          fontSize: "0.875rem",
        },
      },
    },

    "&:hover": {
      backgroundColor: theme.vars.palette.action.hover,
    },

    "& svg": {
      fill: theme.vars.palette.menu.text,
      color: theme.vars.palette.menu.text,
    },

    "& .MuiListItemButton-root": {
      height: "100%",
    },
    // We took panel size - icone size to get text size and we force width with it to not have a buggy transition on open
    "& .MuiTypography-root": {
      width: "calc(250px - 65px)",
    },

    "&.active": {
      color: `${theme.vars.palette.text.accentInverted} !important`,

      "& svg": {
        fill: `${theme.vars.palette.text.accentInverted} !important`,
      },
    },
  },
  isActive: {
    color: `${theme.vars.palette.text.accentInverted} !important`,

    "& svg": {
      fill: `${theme.vars.palette.text.accentInverted} !important`,
    },
  },
}));

const NavIcons = memo(
  ({
    open,
    onSubMenuOpen,
    onSubMenuClose,
  }: {
    open: boolean;
    onSubMenuOpen: (item: string, translatedMenu?: string) => void;
    onSubMenuClose: (
      e: React.MouseEvent,
      drawer: string,
      isItem?: boolean
    ) => void;
  }) => {
    const { mode } = useColorScheme();
    const currentUser = useRecoilValue(currentUserAtom);
    const intl = useIntl();
    const { classes } = navIconStyles();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openDialog, setOpenDialog] = useState(false);

    const locale = useMemo(() => getCurrentLocale(), []);

    const handleOpenAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleCloseAccountMenu = () => {
      setAnchorEl(null);
    };

    const handleOpenProfileSettings = useCallback(
      () => setOpenDialog(true),
      []
    );
    const handleCloseDialog = useCallback(() => {
      setOpenDialog(false);
      setAnchorEl(null);
      onSubMenuClose(
        { relatedTarget: null } as unknown as React.MouseEvent,
        ".sub-drawer"
      );
    }, [onSubMenuClose]);

    useEffect(() => {
      // Close profil Menu on main menu close
      if (!open) {
        handleCloseAccountMenu();
      }
    }, [open]);

    if (!mode) {
      return null;
    }

    return (
      <>
        <List>
          <ListItem
            disablePadding
            className={classes.menuItem}
            data-has-submenu={true}
            onMouseOver={() => {
              onSubMenuOpen(SubMenuName.SUPPORT);
            }}
            onMouseLeave={(e) => onSubMenuClose(e, ".sub-drawer", true)}
          >
            <ListItemButton
              component={NavLink}
              rel="noopener noreferrer"
              target="_blank"
              to={getUrl(locale, KNOWLEDGE_BASE_MENU_LINKS)}
              sx={[
                {
                  pl: 2.25,
                },
                open
                  ? { justifyContent: "initial" }
                  : { justifyContent: "center" },
              ]}
            >
              <ListItemIcon
                sx={[
                  {
                    minWidth: 0,
                    justifyContent: "center",
                  },
                  open ? { mr: 3 } : { mr: "auto" },
                ]}
              >
                <SyncProblemIcon />
              </ListItemIcon>
              <ListItemText
                primary={intl.formatMessage({ id: SubMenuName.SUPPORT })}
                sx={[open ? { opacity: 1 } : { opacity: 0 }]}
              />
            </ListItemButton>
          </ListItem>
          <ListItem
            disablePadding
            className={classes.menuItem}
            data-has-submenu={true}
            onMouseOver={() => {
              onSubMenuOpen(SubMenuName.MARKETPLACE);
            }}
            onMouseLeave={(e) => onSubMenuClose(e, ".sub-drawer", true)}
          >
            <ListItemButton
              component={NavLink}
              rel="noopener noreferrer"
              target="_blank"
              to={getUrl(locale, MARKETPLACE_MENU_LINKS)}
              sx={[
                {
                  pl: 2.25,
                },
                open
                  ? { justifyContent: "initial" }
                  : { justifyContent: "center" },
              ]}
            >
              <ListItemIcon
                sx={[
                  {
                    minWidth: 0,
                    justifyContent: "center",
                  },
                  open ? { mr: 3 } : { mr: "auto" },
                ]}
              >
                <StoreIcon />
              </ListItemIcon>
              <ListItemText
                primary={intl.formatMessage({ id: SubMenuName.MARKETPLACE })}
                sx={[open ? { opacity: 1 } : { opacity: 0 }]}
              />
            </ListItemButton>
          </ListItem>
          <Divider sx={{ my: 1, borderColor: "menu.divider" }} />
          <ListItem disablePadding className={classes.menuItem}>
            <ListItemButton
              onClick={handleOpenAccountMenu}
              sx={[
                {
                  p: "0 10px 0 14px",
                },
                open
                  ? { justifyContent: "initial" }
                  : { justifyContent: "center" },
              ]}
            >
              <ListItemIcon
                sx={[
                  {
                    minWidth: 0,
                    justifyContent: "center",
                  },
                  open ? { mr: 3 } : { mr: "auto" },
                ]}
              >
                <Avatar
                  alt={
                    currentUser
                      ? `${currentUser.first_name} ${currentUser.last_name}`
                      : ""
                  }
                  src={currentUser?.profile_picture_url ?? undefined}
                  {...(!currentUser?.profile_picture_url
                    ? stringAvatar(
                        currentUser
                          ? `${currentUser.first_name} ${currentUser.last_name}`
                          : ""
                      )
                    : {})}
                  sx={(theme) => ({
                    background: currentUser?.profile_picture_url
                      ? "transparent"
                      : theme.vars.palette.nancie.gradient,
                    color: "menu.text",
                    fontSize: "0.875rem",
                    fontWeight: "bold",
                    height: "30px",
                    width: "30px",
                  })}
                />
              </ListItemIcon>
              <ListItemText
                primary={`${currentUser?.first_name} ${currentUser?.last_name}`}
                sx={[open ? { opacity: 1 } : { opacity: 0 }]}
              />
            </ListItemButton>
          </ListItem>
        </List>
        <ProfileMenu
          anchorEl={anchorEl}
          onClose={handleCloseAccountMenu}
          onOpenProfileSettings={handleOpenProfileSettings}
        />
        <ProfileSettingsDialog open={openDialog} onClose={handleCloseDialog} />
      </>
    );
  }
);

export default NavIcons;
